import { logException } from 'lib/errors'
import moment from 'moment'
import { get, set, isArray } from 'lodash'
import * as Nope from 'lib/utils/validation/Nope'
import * as Yup from 'yup'
import { EVENT_FORM_MODE } from 'components/Events/EventForm/EventForm'
import { CurrencyFormatter } from 'lib/utils/currency'

export const EVENT_MODULES = Object.freeze({
    TITLE: 'title',
    COMMENT: 'comment',
    ATTACHMENTS: 'attachments',
    MILEAGE: 'mileage',
    EVENT_TIME: 'eventTime',
    TRAFI_EVENT_TIME: 'trafiEventTime',
    CONFIRMED_AT: 'confirmedAt',
    REMINDER: 'reminder',
    REMIND_NEXT: 'remindNext',
    CREATED: 'createdAt',
    INSPECTION_TRAFI_DATA: 'inspectionTrafiData',
    INSPECTION_RESULT: 'inspectionResult',
    INSPECTION_NOTICES: 'inspectionNotices',
    SERVICE_TYPE: 'serviceType',
    LOCATION: 'location',
    EXPENSE: 'expense',
    INCOME: 'income',
    BOS: 'bos',
    PURCHASE_INFO: 'purchaseInfo',
    REFILL: 'refill',
    VEHICLE_ADDED: 'vehicleAdded',
    PRICE_ESTIMATE: 'priceEstimate',
    NEW_SALES_AD: 'newSalesAd',
    AD_STATUS_CHANGE: 'adStatusChange',
    AD_TIP: 'adTip',
})

const shouldRenderInFormFunctions = Object.freeze({
    // When we are creating new future reminder event, we do not wan't to render some modules that are irrelevant at that time.
    // eg. user does not know inspection result, mileage or expense at the time of creation of future reminder event.
    inPastOrNotCreateEventMode: ({ formMode, eventInPast }) => {
        return ![EVENT_FORM_MODE.CREATE_EVENT].includes(formMode) || eventInPast
    },
    notCreateReminderMode: ({ formMode }) => {
        return formMode !== EVENT_FORM_MODE.CREATE_REMINDER
    },
    noTrafiInspectionDate: ({ form }) => {
        return !form.values.trafiInspectionDate
    },
    hasTrafiInspectionDate: ({ form }) => {
        return !!form.values.trafiInspectionDate
    },
    hasTrafiInspectionResult: ({ form }) => {
        return !!form.values.trafiInspectionResult
    },
    noTrafiInspectionResult: ({ form }) => {
        return !form.values.trafiInspectionResult
    },
})

class Module {
    type
    icon = 'check'
    fieldNames
    formShouldRenderFunctions = []

    constructor (type) {
        this.type = type
        this.fieldNames = [this.type]
    }

    requireFields (fieldNames) {
        for (const key in fieldNames) {
            const field = fieldNames[key]
            if (typeof this[field] === 'undefined') {
                console.error(this)
                logException(`Required module value missing on "${this.type}" type of module: ${field}`)
            }
        }
    }

    isEmpty () {
        throw new Error('isEmpty not implemented')
    }

    getFormattedValue () {
        throw new Error('isEmpty not implemented')
    }

    writeFieldsToObject (object) {
        for (const fieldName of this.fieldNames) {
            set(object, fieldName, this[fieldName] !== null ? this[fieldName] : '')
        }
        return object
    }

    shouldRenderInForm (builderProps) {
        let result = true
        for (const formShouldRenderFunction of this.formShouldRenderFunctions) {
            if (!formShouldRenderFunction(builderProps)) {
                result = false
                break
            }
        }
        return result
    }
}

export class ValueModule extends Module {
    valuePath

    constructor (name, event, valuePath) {
        super(name)

        this.valuePath = valuePath
        this[this.fieldNames[0]] = get(event, valuePath, undefined)

        this.requireFields(this.fieldNames)
    }

    getValue () {
        return this[this.fieldNames[0]]
    }

    isEmpty () {
        const value = this.getValue()
        return !value || (isArray(value) && value.length === 0)
    }

    getFormattedValue () {
        return this.getValue()
    }

    writeValidationToObject (formMode, object) {
        if (this.formValidation) {
            const validation = this.formValidation()
            if (validation) set(object, this.fieldNames[0], validation)
        }

        return object
    }
}

export class DateModule extends ValueModule {
    getFormattedValue (format = 'DD.MM.YYYY HH:mm') {
        const value = this.getValue()
        return moment(value).format(format)
    }

    formValidation () {
        return null
    }
}

export class Comment extends ValueModule {
    constructor (event) {
        super(EVENT_MODULES.COMMENT, event, 'comment')
        this.icon = ['far', 'comment']
    }

    formValidation () {
        return Nope.message(5000)
    }
}

export class Title extends ValueModule {
    constructor (event) {
        super(EVENT_MODULES.TITLE, event, 'title')
    }

    formValidation () {
        return Nope.shortTitle()
    }
}

export class Attachments extends ValueModule {
    constructor (event) {
        super(EVENT_MODULES.ATTACHMENTS, event, 'attachments')
        this.icon = 'paperclip'
    }
}

export class Mileage extends ValueModule {
    constructor (event) {
        super(EVENT_MODULES.MILEAGE, event, 'eventData.mileage')
        this.icon = ['far', 'tachometer-fast']

        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.inPastOrNotCreateEventMode)
        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.notCreateReminderMode)
    }

    getFormattedValue () {
        const value = this.getValue()
        return value ? `${value} km` : ''
    }

    formValidation () {
        return Nope.positiveInteger()
    }
}

export class Expense extends ValueModule {
    constructor (event) {
        super(EVENT_MODULES.EXPENSE, event, 'eventData.expense')
        this.icon = 'euro-sign'

        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.inPastOrNotCreateEventMode)
        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.notCreateReminderMode)
    }

    getFormattedValue () {
        const value = this.getValue()

        return value ? `-${CurrencyFormatter.format(value)}` : ''
    }

    formValidation () {
        return Nope.positivePrice()
    }
}

export class Refill extends Module {
    constructor (event) {
        super(EVENT_MODULES.REFILL, event)
        this.icon = 'gas-pump'

        this.fuelPrice = get(event, 'eventData.fuelPrice', '') || ''
        this.fuelTotalPrice = get(event, 'eventData.fuelTotalPrice', '') || ''
        this.fuelAmount = get(event, 'eventData.fuelAmount', '') || ''
        this.fuelFilledUp = get(event, 'eventData.fuelFilledUp', false) || false
        this.fuelConsumption = get(event, 'eventData.fuelConsumption', false)
        this.isFirstRefillEvent = get(event, 'firstRefillEvent', false) === parseInt(event.id)

        this.fieldNames = [
            'fuelPrice',
            'fuelTotalPrice',
            'fuelAmount',
            'fuelFilledUp',
        ]
    }

    isEmpty () {
        if (this.isFirstRefillEvent) return false
        return !this.fuelConsumption
    }

    getFormattedValue () {
        return 'not defined'
    }

    writeValidationToObject (formMode, object) {
        set(object, 'fuelPrice', Nope.positivePrice())
        set(object, 'fuelTotalPrice', Nope.positivePrice())
        set(object, 'fuelAmount', Nope.positiveNumber())
        set(object, 'fuelFilledUp', Nope.boolean())

        return object
    }
}

export class EventTime extends DateModule {
    constructor (event) {
        super(EVENT_MODULES.EVENT_TIME, event, 'eventTime')

        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.noTrafiInspectionDate)
    }

    writeValidationToObject (formMode, object) {
        switch (formMode) {
        case EVENT_FORM_MODE.CONFIRM:
            set(object, 'eventTime', Yup.date().nullable()
                .test('dateNotInFuture', 'Päivämäärä ei voi olla tulevaisuudessa', (value) => {
                    return !value || moment(new Date()).isAfter(value)
                })
                .required('formElements:validation.required'))
            break
        case EVENT_FORM_MODE.CREATE_REMINDER:
            set(object, 'eventTime', Yup.date().nullable()
                .test('dateNotInPast', 'Päivämäärä ei voi olla menneisyydessä', (value) => {
                    return !value || moment(new Date()).isBefore(value)
                })
                .required('formElements:validation.required'))
            break
        default:
            set(object, 'eventTime', Yup.date().nullable().required('formElements:validation.required'))
        }

        return object
    }
}

export class TrafiEventTime extends DateModule {
    constructor (event) {
        super(EVENT_MODULES.TRAFI_EVENT_TIME, event, 'eventTime')

        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.hasTrafiInspectionDate)
    }
}

export class ConfirmedAt extends DateModule {
    constructor (event) {
        super(EVENT_MODULES.CONFIRMED_AT, event, 'confirmedAt')
    }
}

export class Reminder extends Module {
    constructor (event) {
        super(EVENT_MODULES.REMINDER)
        this.icon = ['fas', 'bell']

        this.fieldNames = [
            'remindBefore',
            'reminderTopic',
        ]

        this.reminderTopic = get(event, 'reminders.reminderTopic', undefined) || ''
        this.remindBefore = get(event, 'reminders.remindBefore', undefined) || []
        this.icon = ['fas', 'bell']

        this.requireFields(this.fieldNames)
    }

    isEmpty () {
        return !this.remindBefore || !this.remindBefore.length
    }

    writeFieldsToObject (object) {
        object = Module.prototype.writeFieldsToObject.call(this, object)

        // map to format used in select input field
        set(object, 'remindBefore', this.remindBefore ? this.remindBefore.map(reminder => {
            return `${reminder.unitAmount} ${reminder.unit}`
        }) : [])

        return object
    }

    writeValidationToObject (formMode, object) {
        set(object, 'reminderTopic', Yup.string().when('remindBefore', {
            is: (value) => value && value.length,
            then: Yup.string().required('formElements:validation.required'),
        }))
        return object
    }

    shouldRenderInForm ({ formMode, eventInPast }) {
        if (formMode === EVENT_FORM_MODE.CONFIRM) {
            return false
        }

        if (formMode === EVENT_FORM_MODE.CREATE_REMINDER) {
            return true
        } else {
            return !eventInPast
        }
    }
}

export class RemindNext extends Module {
    constructor (event) {
        super(EVENT_MODULES.REMIND_NEXT)
        this.icon = ['fas', 'bell']

        this.fieldNames = [
            'nextRemindBefore',
            'nextReminderTime',
            'nextReminderTopic',
        ]

        this.nextRemindBefore = []
        this.nextReminderTime = get(event, 'reminders.reminderTime', null)
        this.nextReminderTopic = ''
        this.icon = ['fas', 'bell']

        this.requireFields(this.fieldNames)
    }

    writeValidationToObject (formMode, object) {
        set(object, 'nextReminderTime', Yup.date().nullable().when('nextRemindBefore', {
            is: (value) => value && value.length,
            then: Yup.date()
                .test('dateNotInPast', 'Päivämäärä ei voi olla menneisyydessä', (value) => {
                    return !value || moment(new Date()).isBefore(value)
                })
                .required('formElements:validation.required'),
        }))
        set(object, 'nextReminderTopic', Yup.string().when('nextRemindBefore', {
            is: (value) => value && value.length,
            then: Yup.string().required('formElements:validation.required'),
        }))
        return object
    }

    isEmpty () {
        return !this.nextReminderTime
    }

    shouldRenderInForm ({ formMode, wrappedEvent, eventInPast }) {
        return (
            !wrappedEvent.isPending() &&
            formMode !== EVENT_FORM_MODE.CREATE_REMINDER &&
            eventInPast
        )
    }

    writeFieldsToObject (object) {
        set(object, 'nextRemindBefore', this.nextRemindBefore)
        set(object, 'nextReminderTime', this.nextReminderTime)
        set(object, 'nextReminderTopic', this.nextReminderTopic)
        return object
    }
}

export class Created extends DateModule {
    constructor (event) {
        super(EVENT_MODULES.CREATED, event, 'createdAt')
    }
}

export class InspectionTrafiData extends Module {
    constructor (event) {
        super(EVENT_MODULES.INSPECTION_TRAFI_DATA, event)

        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.hasTrafiInspectionResult)

        this.trafiData = this.trafiData || {}
        this.trafiInspectionResult = get(event, 'eventData.trafiData.inspectionResult', null) || null
        this.trafiInspectionDate = get(event, 'eventData.trafiData.inspectionDate', null) || null

        this.fieldNames = ['trafiInspectionResult', 'trafiInspectionDate']

        this.icon = 'check-circle'
    }

    isEmpty () {
        return !this.trafiInspectionResult
    }
}

export class InspectionResult extends DateModule {
    constructor (event) {
        super(EVENT_MODULES.INSPECTION_RESULT, event, 'eventData.inspectionResult')

        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.noTrafiInspectionResult)
        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.inPastOrNotCreateEventMode)
        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.notCreateReminderMode)

        this.icon = 'check-circle'
    }

    formValidation () {
        return Nope.message()
    }
}

export class InspectionNotices extends ValueModule {
    constructor (event) {
        super(EVENT_MODULES.INSPECTION_NOTICES, event, 'eventData.inspectionNotices')

        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.inPastOrNotCreateEventMode)
        this.formShouldRenderFunctions.push(shouldRenderInFormFunctions.notCreateReminderMode)

        this.icon = ['far', 'comment']
    }
}

export class Bos extends Module {
    constructor (event) {
        super(EVENT_MODULES.BOS)

        this.fieldNames = ['bosId']

        this.icon = ['fal', 'file-alt']

        const systemEventData = event.eventData.systemEventData ? JSON.parse(event.eventData.systemEventData) : null
        this.bosId = get(systemEventData, 'bosId', null)
    }

    isEmpty () {
        return !this.bosId
    }
}

export class PurchaseInfo extends Module {
    constructor (event) {
        super(EVENT_MODULES.PURCHASE_INFO)

        this.fieldNames = ['purchaseDate', 'purchasePrice', 'purchaseSellerName', 'purchaseNote']

        this.icon = ['fas', 'handshake-alt']

        const systemEventData = event.eventData.systemEventData ? JSON.parse(event.eventData.systemEventData) : null
        this.purchaseDate = get(systemEventData, 'purchaseDate', null)
        this.purchasePrice = get(systemEventData, 'purchasePrice', null)
        this.purchaseSellerName = get(systemEventData, 'purchaseSellerName', null)
        this.purchaseNote = get(systemEventData, 'purchaseNote', null)
    }

    isEmpty () {
        return !this.purchaseDate
    }
}

export class ServiceType extends Module {
    constructor (event) {
        super(EVENT_MODULES.SERVICE_TYPE)

        this.fieldNames = ['serviceType', 'serviceTypeOtherText']

        this.serviceType = get(event, 'eventData.serviceType', undefined)
        this.serviceTypeOtherText = get(event, 'eventData.serviceTypeOtherText', undefined)
        this.icon = 'wrench'

        this.requireFields(this.fieldNames)
    }

    isEmpty () {
        return !this.serviceType
    }
}

export class PriceEstimate extends Module {
    constructor (event) {
        super(EVENT_MODULES.PRICE_ESTIMATE)
        this.icon = 'tag'

        const systemEventData = event.eventData.systemEventData ? JSON.parse(event.eventData.systemEventData) : null
        this.priceCloudUrl = get(systemEventData, 'priceCloudUrl', null)

        this.fieldNames = ['priceCloudUrl']
    }

    isEmpty () {
        return false
    }
}

export class Location extends Module {
    constructor (event) {
        super(EVENT_MODULES.LOCATION)

        this.fieldNames = [
            'locationLatitude',
            'locationLongitude',
            'locationGooglePlaceId',
            'locationGooglePlaceUrl',
            'locationName',
            'locationVicinity',
            'locationAddressFormatted',
            'locationAddressComponents',
        ]

        this.locationLatitude = event.location ? get(event, 'location.latitude', undefined) : null
        this.locationLongitude = event.location ? get(event, 'location.longitude', undefined) : null
        this.locationGooglePlaceId = event.location ? get(event, 'location.googlePlaceId', undefined) : null
        this.locationGooglePlaceUrl = event.location ? get(event, 'location.googlePlaceUrl', undefined) : null
        this.locationName = event.location ? get(event, 'location.locationName', undefined) : null
        this.locationVicinity = event.location ? get(event, 'location.vicinity', undefined) : null
        this.locationAddressFormatted = event.location ? get(event, 'location.addressFormatted', '') : null
        this.locationAddressComponents = event.location ? get(event, 'location.addressComponents', '') : null
        this.icon = 'map-marker-alt'

        this.requireFields(this.fieldNames)
    }

    isEmpty () {
        return !this.locationLatitude && !this.locationLongitude && !this.locationName
    }
}

export class VehicleAdded extends Module {
    constructor (event) {
        super(EVENT_MODULES.VEHICLE_ADDED)

        this.fieldNames = []
        this.icon = 'check'

        this.requireFields(this.fieldNames)
    }

    isEmpty () {
        return false
    }
}

export class AdStatusChange extends Module {
    constructor (event) {
        super(EVENT_MODULES.AD_STATUS_CHANGE)

        this.fieldNames = ['adId', 'adStatus', 'prevAdStatus', 'adUrl', 'vertical']
        this.icon = 'tag'

        const systemEventData = event.eventData.systemEventData ? JSON.parse(event.eventData.systemEventData) : null
        this.adId = get(systemEventData, 'adId', null)
        this.adStatus = get(systemEventData, 'adStatus', null)
        this.prevAdStatus = get(systemEventData, 'prevAdStatus', null)
        this.adUrl = get(systemEventData, 'adUrl', null)
        this.vertical = get(systemEventData, 'vertical', null)

        this.requireFields(this.fieldNames)
    }

    isEmpty () {
        return false
    }
}

export class NewSalesAd extends Module {
    constructor (event) {
        super(EVENT_MODULES.NEW_SALES_AD)

        this.fieldNames = ['isActive', 'adUrl', 'vertical']
        this.icon = 'tag'

        const systemEventData = event.eventData.systemEventData ? JSON.parse(event.eventData.systemEventData) : null
        this.isActive = get(systemEventData, 'isActive', null)
        this.adUrl = get(systemEventData, 'adUrl', null)
        this.vertical = get(systemEventData, 'vertical', null)

        this.requireFields(this.fieldNames)
    }

    isEmpty () {
        return false
    }
}

export class AdTip extends Module {
    constructor (event) {
        super(EVENT_MODULES.AD_TIP)
        this.icon = 'tag'
        this.fieldNames = []
    }

    isEmpty () {
        return false
    }
}
